import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { PreviousPanelButton } from './PreviousPanelButton';
import { MainNavigationGroup } from '../MainNavigationGroup/MainNavigationGroup';
import { RefetchBlock } from '../../../../components/molecules/RefetchBlock';
import { GlobalDocumentIds } from '../../../../types/GlobalDocumentIds';
import { TopItem } from '../MainNavigationTopItem/TopItem';
import { CloseSubPanel, FetchSubPanel, MainNavigationGroupProps, PanelStateType } from '../../MainNavigationTypes';
import { SubPanelLoadingSpinner } from './SubPanelLoadingSpinner';
import { CloseAllSubPanelsButton } from './CloseAllSubPanelsButton';

type SubPanelType = {
  classIndex: number;
  closeSubPanel: CloseSubPanel;
  fetchSubPanel: FetchSubPanel;
  subPanelState: PanelStateType;
  closeAllSubPanels: () => void;
  panelLevel?: number;
  allPanelCount?: number;
};

export function SubPanel({
  classIndex,
  closeSubPanel,
  fetchSubPanel,
  subPanelState,
  closeAllSubPanels,
  panelLevel,
  allPanelCount,
}: SubPanelType) {
  const headlineLinkRef = useRef<HTMLAnchorElement>(null);
  const [activeOpenButtonId, setActiveOpenButtonId] = useState<string | undefined>(undefined);
  const panelNavigationStartingPoint = useRef<string>(undefined);

  /**
   * If next sub panel is closed, activeOpenButtonId of this panel  will be set to undefined,
   * so it is possible to close NextPanelButton without losing focus.
   * */
  useEffect(() => {
    if (panelNavigationStartingPoint.current) {
      document.getElementById(panelNavigationStartingPoint.current)?.focus();
      panelNavigationStartingPoint.current = undefined;
    }

    if (panelLevel === allPanelCount && activeOpenButtonId) {
      panelNavigationStartingPoint.current = activeOpenButtonId;
      setActiveOpenButtonId(undefined);
    }
  }, [allPanelCount, panelLevel, activeOpenButtonId]);

  /**
   * Focusing the sub panel's headline when sub panel is opened
   */
  useEffect(() => {
    headlineLinkRef?.current?.focus();
  }, [headlineLinkRef?.current]);

  const refetchBlock = () => {
    if (
      subPanelState.fetchUrl &&
      subPanelState.panelName &&
      subPanelState.panelLink &&
      subPanelState.nextPanelButtonRef
    ) {
      fetchSubPanel(
        subPanelState.fetchUrl,
        subPanelState.panelLevel,
        subPanelState.panelName,
        subPanelState.panelLink,
        subPanelState.nextPanelButtonRef,
        subPanelState.loadingError,
      );
    }
  };

  return (
    <div
      id={`${GlobalDocumentIds.mainNavigationSubPanel}${classIndex}`}
      className={`main-navigation__sub-panel main-navigation__sub-panel--${classIndex}`}
    >
      <TopItem>
        {subPanelState?.panelLink && subPanelState?.panelName && subPanelState?.fetchUrl && (
          <>
            <PreviousPanelButton
              // @ts-expect-error: should expect string, string will never be undefined, except on loading errors
              panelName={subPanelState?.panelData?.parent?.navItem?.title}
              closeSubPanel={closeSubPanel}
              panelLevel={subPanelState.panelLevel}
            />
            <h2 className="main-navigation__sub-panel-headline">
              <Link
                to={subPanelState.panelLink}
                ref={headlineLinkRef}
                className="main-navigation__sub-panel-headline-link"
              >
                {subPanelState.panelName}
              </Link>
            </h2>
          </>
        )}
        {/* show close button only on last panel */}
        {panelLevel === allPanelCount && <CloseAllSubPanelsButton closeAllSubPanels={closeAllSubPanels} />}
      </TopItem>
      {/* loading */}
      <div className="main-navigation__scroll-container">
        {!subPanelState?.panelData && !subPanelState.loadingError && <SubPanelLoadingSpinner />}
        {/* loading error */}
        {!subPanelState?.panelData && subPanelState?.loadingError && subPanelState?.fetchUrl && (
          <RefetchBlock modifier="application" refetchBlock={refetchBlock} />
        )}
        {subPanelState.panelData?.groups?.map((mainNavigationGroup: MainNavigationGroupProps) => (
          <MainNavigationGroup
            key={mainNavigationGroup.groupLabel}
            groupLabel={mainNavigationGroup.groupLabel}
            navItems={mainNavigationGroup.navItems}
            fetchSubPanel={fetchSubPanel}
            panelLevel={subPanelState.panelLevel + 1}
            activeOpenButtonId={activeOpenButtonId}
            setActiveOpenButtonId={setActiveOpenButtonId}
          />
        ))}
      </div>
    </div>
  );
}
